import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { useContent } from "@/context/ContentContext";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Services() {
  const { content } = useContent();
  const services = content.services || [];
  const hero = content.hero || {};

  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="relative bg-gray-950">
        <div className="absolute inset-0 opacity-30">
          <img src={hero.image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-gray-500" />
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-gray-400">Our Services</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl leading-[1.03]">
            End-to-end commercial construction.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-gray-300 max-w-2xl leading-relaxed">
            From first plans to final walkthrough, we cover the full range of commercial work, keeping scope,
            schedule, and quality under one accountable team.
          </p>
        </div>
      </section>

      {/* Services grid with photos */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div key={s.id} data-testid={`service-${s.id}`} initial="hidden" whileInView="show"
              viewport={{ once: true, amount: 0.15 }} custom={i} variants={fadeUp}
              className="group border border-gray-200 bg-white overflow-hidden hover:border-gray-900 transition-colors duration-300">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={s.image} alt={s.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-500" />
                <span className="absolute top-3 left-3 font-heading text-xs font-bold text-white/90 bg-gray-950/60 backdrop-blur-sm px-2.5 py-1 rounded-sm">
                  0{i + 1}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-semibold text-gray-900">{s.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}

          {/* CTA cell */}
          <Link to="/contact" data-testid="services-cta-cell"
            className="group border border-gray-200 bg-gray-950 flex flex-col justify-between p-8 hover:bg-gray-800 transition-colors duration-300 min-h-[280px]">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">
              Not sure where to start?
            </span>
            <span className="font-heading text-2xl font-semibold text-white inline-flex items-center gap-2">
              Request a Consultation
              <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform duration-200" />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
